// Première étape du dépôt : conditions d'éligibilité, identité vérifiée si la démarche l'exige,
// au nom de qui déposer (soi-même ou une représentation validée), brouillons à reprendre.
import { Bandeau, Bouton, CaseACocher } from '@cc/ui';
import { useState } from 'react';
import { Link } from 'react-router';
import { dateHeure } from '../../../commun/format';
import { Ligne, Liste, Section } from '../commun';
import type { FicheDemarche, ProfilUsager, Representation, ResumeDossierUsager } from '../types';
import styles from '../citoyen.module.css';

export interface ProprietesEtapeEligibilite {
  fiche: FicheDemarche;
  profil: ProfilUsager;
  representations: Representation[];
  brouillons: ResumeDossierUsager[];
  onCommencer: (representationId: string | null) => void;
  enCours: boolean;
  erreur: string | null;
}

export function EtapeEligibilite({ fiche, profil, representations, brouillons, onCommencer, enCours, erreur }: ProprietesEtapeEligibilite) {
  const [representation, setRepresentation] = useState<string | null>(null);
  const [confirme, setConfirme] = useState(false);
  const valides = representations.filter((r) => r.valide && r.collectivite.code === fiche.collectivite.code);
  const identiteBloquante = fiche.identite_verifiee_requise && !profil.identite_verifiee;
  const regles = fiche.conditions_eligibilite.regles;
  return (
    <div className={styles.pile}>
      {brouillons.length > 0 ? (
        <Section titre="Reprendre un brouillon">
          <Liste libelle="Brouillons">
            {brouillons.map((b) => (
              <Ligne
                key={b.id}
                href={`/citoyen/${fiche.collectivite.code}/demarches/${fiche.code}/deposer?dossier=${b.id}`}
                titre={`Brouillon commencé le ${dateHeure(b.cree_le)}`}
                sousTitre={b.representation ? `Au nom de ${b.representation.entite_nom}` : 'En votre nom'}
                droite="Reprendre"
              />
            ))}
          </Liste>
        </Section>
      ) : null}
      <section className={styles.carte} aria-labelledby="eligibilite-titre">
        <h2 id="eligibilite-titre">Vérifier que vous pouvez déposer</h2>
        {fiche.conditions_eligibilite.texte ? <p>{fiche.conditions_eligibilite.texte}</p> : null}
        {regles.length > 0 ? (
          <ul>
            {regles.map((r) => (
              <li key={r.message}>{r.message}</li>
            ))}
          </ul>
        ) : null}
        {!fiche.conditions_eligibilite.texte && regles.length === 0 ? <p>Cette démarche ne pose pas de condition particulière.</p> : null}
        {regles.length > 0 ? <p className={styles.aide}>Ces conditions sont vérifiées au dépôt à partir de vos réponses : une demande qui ne les remplit pas est refusée avant d&apos;être transmise.</p> : null}
        {fiche.identite_verifiee_requise ? (
          identiteBloquante ? (
            <Bandeau type="attention" titre="Identité vérifiée requise">
              Cette démarche exige une identité vérifiée au guichet. La vôtre est déclarée : <Link to="/citoyen/compte/identite">voir comment la faire vérifier</Link>. Vous pouvez préparer le dossier, mais pas le déposer.
            </Bandeau>
          ) : (
            <p className={styles.statutOk}>Votre identité est vérifiée : vous pouvez déposer cette démarche.</p>
          )
        ) : null}
      </section>
      <section className={styles.carte} aria-labelledby="demandeur-titre">
        <h2 id="demandeur-titre">Au nom de qui déposer</h2>
        {valides.length === 0 ? (
          <p>
            En votre nom : {[profil.prenoms, profil.nom].filter(Boolean).join(' ')}. <Link to="/citoyen/compte/representations">Ajouter une entreprise ou une association</Link>
          </p>
        ) : (
          <div role="group" aria-label="Demandeur" className={styles.bascule}>
            <button type="button" aria-pressed={representation === null} onClick={() => setRepresentation(null)}>
              Moi
            </button>
            {valides.map((r) => (
              <button key={r.id} type="button" aria-pressed={representation === r.id} onClick={() => setRepresentation(r.id)}>
                {r.entite_nom}
              </button>
            ))}
          </div>
        )}
      </section>
      <CaseACocher libelle="J'ai lu les conditions et je souhaite préparer cette demande" checked={confirme} onChange={(e) => setConfirme(e.target.checked)} />
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
      <Bouton pleineLargeur disabled={!confirme} chargement={enCours} libelleChargement="Création du brouillon en cours" onClick={() => onCommencer(representation)}>
        Commencer la demande
      </Bouton>
    </div>
  );
}
